<template>
    <b-container id="copy-attachments-page" fluid>
        <b-row>
            <b-col cols="12">
                <div class="border-bottom mb-3 pb-3">
                    <h3>이전 캠페인 첨부파일 복사</h3>
                    <p class="fw-400 text-secondary mb-0">
                        프로젝트를 선택하여, 해당 프로젝트의 이전 캠페인 첨부파일을 현재 캠페인으로 복사합니다.
                        복사 대상은 기업별 sub_code 매핑이 일치하는 첨부파일이며, 실행 전 미리보기로 목록을 확인할 수 있습니다.
                    </p>
                </div>
            </b-col>
        </b-row>

        <b-row class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">프로젝트 선택</h5>
                        </div>
                    </template>
                    <div class="p-3">
                        <b-alert variant="info" show class="mb-3">
                            <strong>안내:</strong> 현재 접속한 템플릿을 기준으로 동일한 템플릿 코드를 사용하는 프로젝트의 이전 캠페인 첨부파일이 복사됩니다.
                        </b-alert>
                        <b-form @submit.stop.prevent="onPreview">
                            <b-row>
                                <b-col cols="12" md="6" lg="4">
                                    <b-form-group
                                        label="프로젝트"
                                        label-for="input-project-code"
                                    >
                                        <auto-complete-select
                                            ref="projectCodeAutoComplete"
                                            v-model="selectedProjectId"
                                            :options="projectCodeOptions"
                                            start-expanded
                                            list-max-height="360px"
                                            option-value-key="id"
                                            placeholder="프로젝트를 선택하세요"
                                            :disabled="isBusy"
                                            clearable
                                            @input="onProjectSelected"
                                        />
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            <div class="d-flex align-items-center">
                                <b-button
                                    type="submit"
                                    variant="outline-primary"
                                    class="mr-2"
                                    :disabled="!projectCode || isBusy"
                                >
                                    <b-spinner v-if="previewLoading" small class="mr-1"/>
                                    미리보기
                                </b-button>
                                <b-button
                                    variant="primary"
                                    :disabled="!projectCode || isBusy"
                                    @click="onExecute"
                                >
                                    <b-spinner v-if="executing" small class="mr-1"/>
                                    실행
                                </b-button>
                                <b-button
                                    v-if="previewResult || trackedTasks.length > 0"
                                    variant="link"
                                    class="ml-2"
                                    :disabled="isBusy"
                                    @click="resetAll"
                                >
                                    초기화
                                </b-button>
                            </div>
                        </b-form>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-row v-if="previewResult" class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">미리보기 결과</h5>
                            <b-button
                                v-if="allPreviewRows.length > 0"
                                variant="outline-success"
                                size="sm"
                                @click="downloadPreviewCsv"
                            >
                                CSV 다운로드
                            </b-button>
                        </div>
                    </template>
                    <div class="p-3">
                        <b-row class="mb-3">
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">현재 캠페인 ID</p>
                                    <p class="h5 mb-0">{{ previewResult.campaignId || '-' }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">이전 캠페인 ID</p>
                                    <p class="h5 mb-0">{{ previewPreviousCampaignId }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">대상 기업 수</p>
                                    <p class="h5 mb-0">{{ previewCompanyCount }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">복사 예상</p>
                                    <p class="h5 mb-0 text-success">{{ previewCopyCount }}</p>
                                </div>
                            </b-col>
                            <b-col v-if="previewSkipCount > 0" cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">스킵 예상</p>
                                    <p class="h5 mb-0 text-warning">{{ previewSkipCount }}</p>
                                </div>
                            </b-col>
                        </b-row>

                        <div v-if="previewTotalRows > 0">
                            <h6 class="mb-2">
                                전체 {{ previewTotalRows }}건
                                <small class="text-secondary">(복사 {{ previewCopyCount }} / 스킵 {{ previewSkipCount }})</small>
                            </h6>
                            <b-table
                                id="copy-attachments-preview-table"
                                :items="previewRows"
                                :fields="previewFields"
                                small
                                striped
                                responsive
                                class="mb-0"
                            >
                                <template #cell(action)="{ item }">
                                    <b-badge :variant="item.action === 'copy' ? 'success' : 'warning'">
                                        {{ item.action === 'copy' ? '복사' : '스킵' }}
                                    </b-badge>
                                </template>
                                <template #cell(reason)="{ item }">
                                    <small v-if="item.reason" class="text-secondary">{{ item.reason }}</small>
                                    <span v-else>-</span>
                                </template>
                            </b-table>
                            <div class="d-flex justify-content-between align-items-center flex-wrap mt-3">
                                <p class="small text-secondary mb-2 mb-md-0">
                                    총 {{ previewTotalRows }}건 중 {{ previewPageStart }}-{{ previewPageEnd }}건 표시
                                </p>
                                <div class="d-flex align-items-center flex-wrap justify-content-end">
                                    <b-pagination
                                        v-model="previewPagination.currentPage"
                                        aria-controls="copy-attachments-preview-table"
                                        :total-rows="previewTotalRows"
                                        :per-page="previewPagination.perPage"
                                        first-number
                                        last-number
                                        align="right"
                                        size="sm"
                                        class="mb-0"
                                    />
                                    <b-form-select
                                        v-model="previewPagination.perPage"
                                        class="ml-3"
                                        style="width: 100px"
                                        size="sm"
                                        @change="handlePreviewPerPageChange"
                                    >
                                        <b-form-select-option
                                            v-for="option in perPageOptions"
                                            :key="`${option.text}-${option.value}`"
                                            :value="option.value"
                                        >
                                            {{ option.text }}
                                        </b-form-select-option>
                                    </b-form-select>
                                </div>
                            </div>
                        </div>
                        <p v-else class="text-secondary mb-0">복사할 파일이 없습니다.</p>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-row v-if="trackedTasks.length > 0" class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">실행 현황</h5>
                            <b-badge v-if="activeTaskCount > 0" variant="info">
                                {{ activeTaskCount }}건 진행 중
                            </b-badge>
                        </div>
                    </template>
                    <div class="p-3">
                    <b-table
                        :items="trackedTasks"
                        :fields="trackedTaskFields"
                        small
                        striped
                        responsive
                        class="mb-0"
                    >
                        <template #cell(status)="{ item }">
                            <b-badge :variant="getTaskStatusVariant(item.status)">{{ item.status }}</b-badge>
                            <b-badge v-if="item.checkFailed" variant="warning" class="ml-1">상태 확인 실패</b-badge>
                        </template>
                        <template #cell(progress)="{ item }">
                            <div v-if="item.status === 'FAILURE' || item.status === 'REVOKED'">
                                <small class="text-danger">{{ item.errorMessage || '실행 실패' }}</small>
                            </div>
                            <div v-else-if="item.progress && item.progress.total > 0" style="min-width: 120px;">
                                <b-progress :max="item.progress.total" height="8px" :animated="!isTerminalStatus(item.status)">
                                    <b-progress-bar :value="item.progress.current" :variant="getTaskStatusVariant(item.status)"/>
                                </b-progress>
                                <small class="text-secondary">{{ item.progress.current }} / {{ item.progress.total }}</small>
                            </div>
                            <small v-else class="text-secondary">대기 중</small>
                        </template>
                        <template #cell(startedAt)="{ item }">
                            <small class="text-secondary">{{ formatTaskTime(item.startedAt) }}</small>
                        </template>
                        <template #cell(actions)="{ item }">
                            <b-button
                                v-if="item.status === 'SUCCESS'"
                                variant="link"
                                size="sm"
                                class="p-0 mr-2"
                                @click="showResult(item.taskId)"
                            >
                                결과 보기
                            </b-button>
                            <b-button
                                v-if="item.checkFailed"
                                variant="link"
                                size="sm"
                                class="p-0 mr-2"
                                @click="retryPolling(item.taskId)"
                            >
                                재시도
                            </b-button>
                            <b-button
                                v-if="isTerminalStatus(item.status) || item.checkFailed"
                                variant="link"
                                size="sm"
                                class="p-0 text-secondary"
                                @click="dismissTrackedTask(item.taskId)"
                            >
                                닫기
                            </b-button>
                        </template>
                    </b-table>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <b-row v-if="finalResult" class="mb-3">
            <b-col cols="12">
                <b-card no-body bg-variant="white">
                    <template #header>
                        <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">실행 결과</h5>
                            <b-button
                                variant="outline-success"
                                size="sm"
                                :disabled="csvDownloading"
                                @click="downloadResultCsv"
                            >
                                <b-spinner v-if="csvDownloading" small class="mr-1"/>
                                CSV 다운로드
                            </b-button>
                        </div>
                    </template>
                    <div class="p-3">
                        <b-row class="mb-3">
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">복사 완료</p>
                                    <p class="h5 mb-0 text-success">{{ finalCopiedCount }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">스킵</p>
                                    <p class="h5 mb-0 text-warning">{{ finalSkippedCount }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">대상 기업</p>
                                    <p class="h5 mb-0">{{ finalCompanyCount }}</p>
                                </div>
                            </b-col>
                            <b-col cols="6" md="3">
                                <div class="summary-box">
                                    <p class="small text-secondary mb-1">이전 캠페인 ID</p>
                                    <p class="h5 mb-0">{{ finalPreviousCampaignId }}</p>
                                </div>
                            </b-col>
                        </b-row>

                        <div v-if="edgeCases.length > 0" class="mt-3">
                            <h6 class="mb-2">엣지 케이스 ({{ edgeCases.length }}건)</h6>
                            <b-table
                                :items="edgeCases"
                                :fields="edgeCaseFields"
                                small
                                striped
                                responsive
                                class="mb-0"
                            />
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
import AutoCompleteSelect from '@core/components/common/AutoCompleteSelect.vue';
import { getDefaultPagination, getPerPageOptions, getProjectCodeOptions } from '../common-data';

const POLL_INTERVAL_MS = 2000;
const SNAKE_CASE_REGEX = /_([a-z])/g;
const STORAGE_KEY = 'copy-attachments-tracked-tasks';
const TERMINAL_STATUSES = ['SUCCESS', 'FAILURE', 'REVOKED'];

function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
}

function toCamelCaseKey(key) {
    return key.replace(SNAKE_CASE_REGEX, (_, char) => char.toUpperCase());
}

function normalizeCopyAttachmentsResponse(value) {
    if (Array.isArray(value)) {
        return value.map(normalizeCopyAttachmentsResponse);
    }
    if (!isPlainObject(value)) {
        return value;
    }

    return Object.entries(value).reduce((acc, [key, itemValue]) => {
        acc[toCamelCaseKey(key)] = normalizeCopyAttachmentsResponse(itemValue);
        return acc;
    }, {});
}

export default {
    name: 'CopyAttachments',
    components: {
        AutoCompleteSelect,
    },
    data() {
        return {
            projects: null,
            projectsLoading: false,
            selectedProjectId: null,
            projectCode: '',
            previewLoading: false,
            executing: false,
            csvDownloading: false,
            trackedTasks: [],
            pollFails: {},   // taskId → 연속 상태 조회 실패 횟수
            previewPagination: getDefaultPagination(1, 20),
            perPageOptions: getPerPageOptions(),

            previewResult: null,

            taskState: {
                taskId: null,
                status: null,
                progress: null,
                errorMessage: null,
            },
            pollInterval: null,

            finalResult: null,

            previewFields: [
                { key: 'companyName', label: '기업명' },
                { key: 'action', label: '동작' },
                { key: 'oldSubCode', label: '이전 sub_code' },
                { key: 'newSubCode', label: '현재 sub_code' },
                { key: 'filename', label: '파일명' },
                { key: 'reason', label: '사유' },
            ],
            edgeCaseFields: [
                { key: 'companyId', label: '기업 ID' },
                { key: 'companyName', label: '기업명' },
                { key: 'oldSubCode', label: '이전 sub_code' },
                { key: 'newSubCode', label: '현재 sub_code' },
                { key: 'reason', label: '사유' },
                { key: 'filename', label: '파일명' },
            ],
            trackedTaskFields: [
                { key: 'projectCode', label: '프로젝트' },
                { key: 'status', label: '상태' },
                { key: 'progress', label: '진행률' },
                { key: 'startedAt', label: '시작' },
                { key: 'actions', label: '', thStyle: 'width: 60px' },
            ],
        };
    },
    computed: {
        projectCodeOptions() {
            return getProjectCodeOptions(this.projects);
        },
        isBusy() {
            return this.previewLoading || this.executing || this.csvDownloading;
        },
        activeTaskCount() {
            return this.trackedTasks.filter(t => !TERMINAL_STATUSES.includes(t.status)).length;
        },
        isPolling() {
            return this.pollInterval !== null;
        },
        allPreviewRows() {
            if (!this.previewResult) return [];
            return this.previewResult.plannedRows || this.previewResult.previewRows || [];
        },
        previewRows() {
            const start = (this.previewPagination.currentPage - 1) * this.previewPagination.perPage;
            const end = start + this.previewPagination.perPage;
            return this.allPreviewRows.slice(start, end);
        },
        previewTotalRows() {
            return this.allPreviewRows.length || this.previewPlannedCount || 0;
        },
        previewPageStart() {
            if (this.previewTotalRows === 0) return 0;
            return ((this.previewPagination.currentPage - 1) * this.previewPagination.perPage) + 1;
        },
        previewPageEnd() {
            if (this.previewTotalRows === 0) return 0;
            return Math.min(this.previewPagination.currentPage * this.previewPagination.perPage, this.previewTotalRows);
        },
        previewPreviousCampaignId() {
            return this.previewResult?.previousCampaignId || '-';
        },
        previewCompanyCount() {
            return this.previewResult?.companyCount || this.previewResult?.totalCompanies || 0;
        },
        previewPlannedCount() {
            return this.previewResult?.plannedCount || this.previewResult?.totalAttachments || 0;
        },
        previewCopyCount() {
            const rows = this.allPreviewRows;
            if (!rows.length) return this.previewPlannedCount;
            return rows.filter(r => r.action === 'copy').length;
        },
        previewSkipCount() {
            return this.allPreviewRows.filter(r => r.action === 'skip_duplicate').length;
        },
        edgeCases() {
            return this.finalResult?.edgeCases || [];
        },
        finalCopiedCount() {
            return this.finalResult?.copiedCount || 0;
        },
        finalSkippedCount() {
            return this.finalResult?.skippedCount || 0;
        },
        finalCompanyCount() {
            return this.finalResult?.companyCount || this.finalResult?.totalCompanies || 0;
        },
        finalPreviousCampaignId() {
            return this.finalResult?.previousCampaignId || '-';
        },
    },
    async created() {
        await this.fetchProjects();
        this.loadTrackedTasks();
        if (this.activeTaskCount > 0) {
            this.startPolling();
        }
    },
    beforeDestroy() {
        this.stopPolling();
    },
    methods: {
        async fetchProjects() {
            this.projectsLoading = true;
            try {
                const response = await this.$axios.get('/project_code');
                this.projects = response.data;
            } catch (error) {
                console.error('Failed to load projects:', error);
                this.$EventBus.$emit('toast-failure', '프로젝트 로드 실패', '프로젝트 목록을 불러오는 중 오류가 발생했습니다.');
            } finally {
                this.projectsLoading = false;
            }
        },
        onProjectSelected(projectId) {
            if (!projectId || !this.projects) {
                this.projectCode = '';
                return;
            }
            for (const group of this.projects) {
                const project = group.projects?.find(p => p.id === projectId);
                if (project) {
                    this.projectCode = project.project_code;
                    return;
                }
            }
            this.projectCode = '';
        },
        async onPreview() {
            if (!this.projectCode) return;

            this.previewLoading = true;
            this.previewResult = null;
            this.resetPreviewPagination();

            try {
                const res = await this.$axios.post('/admin/copy-attachments/preview', {
                    project_code: this.projectCode,
                });

                if (res.data?.result === 'ok' || res.data?.data) {
                    this.previewResult = normalizeCopyAttachmentsResponse(res.data.data);
                    this.syncPreviewPagination();
                    this.$EventBus.$emit('toast-success', '미리보기 완료', `복사 예상 파일 수: ${this.previewPlannedCount}`);
                } else {
                    this.$EventBus.$emit('toast-failure', '미리보기 실패', res.data?.message || '응답을 해석할 수 없습니다.');
                }
            } catch (error) {
                console.error('Preview error:', error);
                const message = getErrorMessage(error, '') || error.message || '알 수 없는 오류가 발생했습니다.';
                this.$EventBus.$emit('toast-failure', '미리보기 실패', message);
            } finally {
                this.previewLoading = false;
            }
        },
        handlePreviewPerPageChange(value) {
            this.previewPagination.perPage = Number(value) || this.previewPagination.perPage;
            this.previewPagination.currentPage = 1;
            this.syncPreviewPagination();
        },
        resetPreviewPagination() {
            this.previewPagination.currentPage = 1;
            this.previewPagination.totalRows = 0;
        },
        syncPreviewPagination() {
            this.previewPagination.totalRows = this.previewTotalRows;
            const totalPages = Math.max(1, Math.ceil(this.previewPagination.totalRows / this.previewPagination.perPage));
            if (this.previewPagination.currentPage > totalPages) {
                this.previewPagination.currentPage = totalPages;
            }
        },

        async onExecute() {
            if (!this.projectCode) return;

            const confirmed = await this.$bvModal.msgBoxConfirm(
                `프로젝트 [${this.projectCode}]의 이전 캠페인 첨부파일을 복사합니다. 계속하시겠습니까?`,
                {
                    title: '실행 확인',
                    okVariant: 'primary',
                    okTitle: '실행',
                    cancelTitle: '취소',
                    centered: true,
                }
            );
            if (!confirmed) return;

            this.executing = true;
            this.finalResult = null;

            try {
                const res = await this.$axios.post('/admin/copy-attachments/execute', {
                    project_code: this.projectCode,
                });

                const data = normalizeCopyAttachmentsResponse(res.data?.data);
                if (!data?.taskId) {
                    throw new Error(res.data?.message || '태스크 ID를 받지 못했습니다.');
                }

                this.taskState = {
                    taskId: data.taskId,
                    status: data.status || 'PENDING',
                    progress: null,
                    errorMessage: null,
                };

                this.trackedTasks.push({
                    taskId: data.taskId,
                    projectCode: this.projectCode,
                    status: data.status || 'PENDING',
                    progress: null,
                    errorMessage: null,
                    startedAt: new Date().toISOString(),
                });
                this.saveTrackedTasks();

                this.$EventBus.$emit('toast-info', '실행 시작', data.message || '첨부파일 복사 태스크가 시작되었습니다.');

                this.startPolling();
            } catch (error) {
                console.error('Execute error:', error);
                const message = getErrorMessage(error, '') || error.message || '알 수 없는 오류가 발생했습니다.';
                this.$EventBus.$emit('toast-failure', '실행 실패', message);
            } finally {
                this.executing = false;
            }
        },

        startPolling() {
            this.stopPolling();
            this.fetchStatus();
            this.pollInterval = setInterval(() => {
                this.fetchStatus();
            }, POLL_INTERVAL_MS);
        },

        stopPolling() {
            if (this.pollInterval) {
                clearInterval(this.pollInterval);
                this.pollInterval = null;
            }
        },

        async fetchStatus() {
            const activeTasks = this.trackedTasks.filter(t => !TERMINAL_STATUSES.includes(t.status) && !t.checkFailed);
            if (activeTasks.length === 0) {
                this.stopPolling();
                return;
            }
            for (const task of activeTasks) {
                await this.pollSingleTask(task);
            }
        },

        async pollSingleTask(task) {
            try {
                const res = await this.$axios.get(`/admin/copy-attachments/status/${task.taskId}`);
                const data = normalizeCopyAttachmentsResponse(res.data?.data);
                if (!data) return;
                this.pollFails[task.taskId] = 0;

                const idx = this.trackedTasks.findIndex(t => t.taskId === task.taskId);
                if (idx === -1) return;

                this.$set(this.trackedTasks, idx, {
                    ...this.trackedTasks[idx],
                    status: data.status || task.status,
                    progress: data.progress || task.progress,
                    errorMessage: data.errorMessage || null,
                });

                if (data.status === 'SUCCESS') {
                    this.taskState.taskId = task.taskId;
                    // 완료 작업은 목록에 남겨 결과 보기/닫기를 제공한다(결과 조회 실패 시에도 다시 볼 수 있게)
                    await this.fetchResult();
                    this.saveTrackedTasks();
                } else if (data.status === 'FAILURE' || data.status === 'REVOKED') {
                    this.saveTrackedTasks();
                    this.$EventBus.$emit('toast-failure', '실행 실패', `[${task.projectCode}] ${data.errorMessage || '태스크 실행에 실패했습니다.'}`);
                }
            } catch (error) {
                console.error(`Status poll error for ${task.taskId}:`, error);
                // 일시 실패는 다음 틱에서 재시도, 연속 3회 실패하면 중단하고 표시한다
                this.pollFails[task.taskId] = (this.pollFails[task.taskId] || 0) + 1;
                if (this.pollFails[task.taskId] >= 3) {
                    const idx = this.trackedTasks.findIndex(t => t.taskId === task.taskId);
                    if (idx !== -1) this.$set(this.trackedTasks, idx, {...this.trackedTasks[idx], checkFailed: true});
                    this.$EventBus.$emit('toast-failure', '상태 확인 실패', `[${task.projectCode}] 작업 상태를 확인하지 못했습니다. [재시도] 또는 [닫기]를 눌러주세요.`);
                }
            }
        },
        retryPolling(taskId) {
            const idx = this.trackedTasks.findIndex(t => t.taskId === taskId);
            if (idx === -1) return;
            this.pollFails[taskId] = 0;
            this.$set(this.trackedTasks, idx, {...this.trackedTasks[idx], checkFailed: false});
            this.startPolling();
        },
        async showResult(taskId) {
            this.taskState.taskId = taskId;
            await this.fetchResult();
        },

        async fetchResult() {
            try {
                const res = await this.$axios.get(`/admin/copy-attachments/result/${this.taskState.taskId}`);
                this.finalResult = normalizeCopyAttachmentsResponse(res.data?.data) || null;
                this.$EventBus.$emit('toast-success', '실행 완료', `복사 완료: ${this.finalResult?.copiedCount || 0}건`);
            } catch (error) {
                console.error('Result fetch error:', error);
                const message = getErrorMessage(error, '') || error.message || '결과 조회에 실패했습니다.';
                this.$EventBus.$emit('toast-failure', '결과 조회 실패', message);
            }
        },

        loadTrackedTasks() {
            try {
                const stored = localStorage.getItem(STORAGE_KEY);
                if (stored) {
                    this.trackedTasks = JSON.parse(stored);
                }
            } catch (e) {
                this.trackedTasks = [];
            }
        },

        saveTrackedTasks() {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(this.trackedTasks));
            } catch (e) {
                // silently fail
            }
        },

        dismissTrackedTask(taskId) {
            this.trackedTasks = this.trackedTasks.filter(t => t.taskId !== taskId);
            this.saveTrackedTasks();
        },

        isTerminalStatus(status) {
            return TERMINAL_STATUSES.includes(status);
        },

        getTaskStatusVariant(status) {
            const map = {
                PENDING: 'secondary',
                SENT: 'secondary',
                STARTED: 'info',
                SUCCESS: 'success',
                FAILURE: 'danger',
                REVOKED: 'warning',
            };
            return map[status] || 'secondary';
        },

        formatTaskTime(dateStr) {
            if (!dateStr) return '-';
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        },

        downloadPreviewCsv() {
            const rows = this.allPreviewRows;
            if (!rows.length) return;

            const headers = this.previewFields.map(f => f.label);
            const keys = this.previewFields.map(f => f.key);
            const csvRows = [
                headers.join(','),
                ...rows.map(row =>
                    keys.map(key => {
                        const val = String(row[key] ?? '');
                        return `"${val.replace(/"/g, '""')}"`;
                    }).join(',')
                ),
            ];

            const bom = '\uFEFF';
            const blob = new Blob([bom + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `copy_attachments_preview_${this.projectCode || 'unknown'}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        },

        async downloadResultCsv() {
            if (!this.taskState.taskId) return;

            this.csvDownloading = true;
            try {
                const res = await this.$axios.get(
                    `/admin/copy-attachments/result/${this.taskState.taskId}/csv`,
                    { responseType: 'blob' }
                );

                const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `copy_attachments_${this.projectCode || this.taskState.taskId}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);

                this.$EventBus.$emit('toast-success', 'CSV 다운로드', 'CSV 파일 다운로드가 시작되었습니다.');
            } catch (error) {
                console.error('CSV download error:', error);
                this.$EventBus.$emit('toast-failure', 'CSV 다운로드 실패', error.message || '다운로드 중 오류가 발생했습니다.');
            } finally {
                this.csvDownloading = false;
            }
        },

        resetAll() {
            this.stopPolling();
            this.selectedProjectId = null;
            this.projectCode = '';
            this.previewResult = null;
            this.resetPreviewPagination();
            this.finalResult = null;
            this.taskState = {
                taskId: null,
                status: null,
                progress: null,
                errorMessage: null,
            };
            this.trackedTasks = [];
            this.saveTrackedTasks();
        },
    },
};
</script>

<style lang="scss" scoped>
#copy-attachments-page {
    .summary-box {
        background-color: #f8f9fa;
        border-radius: 0.25rem;
        padding: 0.75rem;
        height: 100%;
    }
}
</style>
